import React, { forwardRef, useState } from "react";
import { FaAngleDown } from "react-icons/fa6";
import { Chip } from "primereact/chip";
import { Tooltip } from "primereact/tooltip";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/atoms/ui/popover";
import { cn } from "@/styles/cn";

import { Option } from "./models/MultiAutocomplete.types";
import { MultiAutocompleteInline, MultiAutocompleteInlineProps } from "./MultiAutocompleteInline";

export type MultiAutocompleteProps<T extends Option> = MultiAutocompleteInlineProps<T> & {
  selectedItemClassName?: (x: T) => string;
  selectedItemTooltip?: (x: T) => string | undefined;
};

export const MultiAutocomplete = forwardRef<HTMLButtonElement, MultiAutocompleteProps<Option>>(
  function MultiAutocomplete<T extends Option>(
    {
      invalid,
      disabled,
      variant,
      selectedItemClassName,
      selectedItemTooltip,
      ...props
    }: MultiAutocompleteProps<T>,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) {
    const [open, setOpen] = useState(false);
    const { value, field = "label", placeholder, dataKey, onChange } = props;

    const count = value?.length ?? 0;
    const isPlaceholder = count === 0;

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          ref={ref}
          className={cn(
            "flex h-[40px] w-full min-w-[120px] items-center gap-2",
            "rounded border border-urbe-border bg-white p-2 text-sm text-urbe-gray",
            {
              "border border-urbe-error": invalid,
              "pointer-events-none cursor-default select-none bg-urbe-grayLight": disabled,
              "hover:border-urbe-primary": !disabled && !invalid,
              "bg-urbe-primaryLight": variant === "filled",
              "text-urbe-border": isPlaceholder,
            },
          )}
          tabIndex={0}
        >
          <span className="flex-1 overflow-hidden text-ellipsis text-nowrap text-left">
            <ChipsList
              {...{
                value,
                field,
                placeholder,
                dataKey,
                selectedItemClassName,
                selectedItemTooltip,
              }}
              onChange={onChange}
            />
          </span>
          {count > 0 && (
            <span className="text-md min-w-[1.2rem] text-center text-urbe-primary">+{count}</span>
          )}
          <span className="mr-2 text-right text-urbe-border">
            <FaAngleDown size={14} />
          </span>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="scroll w-auto min-w-full"
          side="bottom"
          sideOffset={1}
          style={{ width: "var(--radix-popover-trigger-width)" }}
        >
          <MultiAutocompleteInline {...props} />
        </PopoverContent>
      </Popover>
    );
  },
) as <T extends Option>(
  props: MultiAutocompleteProps<T> & { ref?: React.Ref<HTMLButtonElement> },
) => React.ReactNode;

function ChipsList<T extends Option>({
  value,
  field,
  dataKey,
  placeholder,
  onChange,
  selectedItemClassName,
  selectedItemTooltip,
}: {
  value: T[];
  field: string;
  dataKey: string;
  placeholder?: string;
  selectedItemClassName?: (x: T) => string;
  selectedItemTooltip?: (x: T) => string | undefined;
  onChange?: (value: T[]) => void;
}) {
  const count = value?.length ?? 0;

  const handleRemove = (item: T) => {
    onChange?.(value.filter((x) => x[dataKey] !== item[dataKey]));
  };

  return count ? (
    <>
      {value.map((x: T, index) => (
        <Chip
          key={(x[dataKey] as string) ?? index}
          className="selectedItemChip"
          data-pr-position="bottom"
          data-pr-tooltip={selectedItemTooltip?.(x)}
          label={x[field] as string}
          pt={{
            root: () => selectedItemClassName?.(x),
          }}
          removable={!!onChange}
          onRemove={(e) => {
            e.originalEvent.stopPropagation();
            handleRemove(x);
            return true;
          }}
        />
      ))}
      <Tooltip className="max-w-[200px]" target=".selectedItemChip" />
    </>
  ) : (
    placeholder
  );
}
